import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { LeadSection } from "@/components/LeadSection";
import { SiteFooter } from "@/components/SiteFooter";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Leistungen: Shop, Anpassungen, Conversion";
const DESCRIPTION =
  "Leistungen als Shopify Freelancer: Shop erstellen lassen, Theme anpassen und entwickeln ab 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.";

export const Route = createFileRoute("/leistungen")({
  component: LeistungenPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/leistungen` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/leistungen` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/leistungen" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/services" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/leistungen" },
    ],
  }),
});

const areas = [
  {
    title: "Shop erstellen lassen",
    text: "Neuer Shopify-Shop von der Struktur bis zum Start, inklusive Theme, Produktseiten, Zahlungen und Versand.",
    price: "Festpreis nach Umfang",
    to: "/shopify-shop-erstellen-lassen" as const,
  },
  {
    title: "Anpassungen & Entwicklung",
    text: "Sections, Produktseiten, Varianten, mobile Darstellung und technische Fehler im vorhandenen Theme.",
    price: "110 € netto pro Stunde",
    to: "/shopify-theme-anpassung" as const,
  },
  {
    title: "Conversion-Optimierung",
    text: "Mehr Bestellungen aus dem Traffic, den Sie schon haben, mit klaren Messgrößen statt Bauchgefühl.",
    price: "Festpreis ab 3.990 € netto",
    to: "/shopify-conversion-optimierung" as const,
  },
];

/** Bausteine innerhalb der Conversion-Arbeit. */
const services = [
  {
    title: "Conversion-Audit",
    text: "Analytics, Heatmaps, Session-Recordings und Checkout-Funnel. Ergebnis: eine nach Euro sortierte Liste, jeder Hebel mit geschätztem Mehrumsatz und Aufwand.",
  },
  {
    title: "Produkt- & Kollektionsseiten",
    text: "Neu gebaut entlang der Kaufentscheidung: Angebotslogik, Beweisführung, Reihenfolge. Ergebnis: mehr Warenkorb-Zugänge aus demselben Traffic.",
  },
  {
    title: "Warenkorb & Checkout",
    text: "Klare Kosten, frühe Versandinfos, passende Zahlarten, mobiler Flow. Welche Eingriffe im Checkout selbst möglich sind, hängt von Ihrem Shopify-Plan ab.",
  },
  {
    title: "Pagespeed & App-Bereinigung",
    text: "Überflüssige Apps raus, Skripte entschlacken, Bilder und Fonts sauber ausliefern. Ergebnis: schnellere Kaufstrecke, spürbar mobil.",
  },
  {
    title: "A/B-Testing",
    text: "Tests nur dort, wo der Traffic sie trägt, eine Hypothese, eine Kennzahl, feste Laufzeit. Ergebnis: Entscheidungen, die halten.",
  },
  {
    title: "Messbarkeit & Auswertung",
    text: "Saubere Datenbasis für Kaufabschlussrate, Warenkorb und Umsatz pro Session. Ergebnis: Sie sehen schwarz auf weiß, was der Sprint gebracht hat.",
  },
];

function LeistungenPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5">
            <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Drei Bereiche, in denen ich Ihren{" "}
              <span className="text-signal">Shopify-Shop</span> voranbringe.
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground">
              Shop aufbauen, bestehendes Theme anpassen und weiterentwickeln, oder mehr
              Bestellungen aus dem vorhandenen Traffic holen. Anpassungen kosten 110 € netto
              pro Stunde, Conversion-Projekte starten bei 3.990 € netto, ein neuer Shop läuft
              über einen Festpreis nach Umfang.
            </p>
            <p className="mt-4 max-w-2xl leading-relaxed text-foreground">
              Das Ziel ist immer gleich: weniger offene Baustellen und ein Shop, der Besucher
              leichter zur Bestellung führt.
            </p>
            <Link
              to="/kontakt"
              className="shadow-signal mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
            </Link>
            <div className="mt-9 grid gap-5 md:grid-cols-3">
              {areas.map((a) => (
                <Link
                  key={a.title}
                  to={a.to}
                  className="shadow-lift rounded-lg border border-border bg-card p-6 transition-transform hover:-translate-y-0.5"
                >
                  <h2 className="text-lg font-semibold">{a.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.text}</p>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                    {a.price}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    Mehr erfahren <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              Was in der Conversion-Arbeit steckt
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Diese Bausteine gehören zu einem Conversion-Projekt. Welche davon greifen,
              entscheidet die Ausgangslage in Ihrem Shop.
            </p>
          </div>
          <div className="mx-auto mt-9 grid max-w-6xl gap-5 px-5 md:grid-cols-3">
            {services.map((s) => (
              <div key={s.title} className="shadow-lift rounded-lg border border-border bg-card p-6">
                <Check className="h-5 w-5 text-primary" />
                <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 text-center">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Passt das zu Ihrem Shop?</h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              In 20 Minuten wissen wir beide, ob sich das für Sie rechnet. Wenn nicht, sage
              ich Ihnen das ehrlich, das ist deutlich günstiger als ein Projekt, das am Ende
              nichts bringt.
            </p>
            <Link
              to="/kontakt"
              className="shadow-signal mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Kostenlose Einschätzung erhalten
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
        <LeadSection source="leistungen" />
      </main>
      <SiteFooter />
    </div>
  );
}
